import api from './api';
import type { ApiResponse, ReportUpdateData, Marker, MarkerUpdateData } from '@/types';

export class ReportService {
  // Get all reports created by the current user
  static async getUserReports(): Promise<{ markers: Marker[] }> {
    const response = await api.get<ApiResponse<{ markers: Marker[] }>>('/markers/my-reports');
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to fetch user reports');
    }

    return response.data.data;
  }

  // Update a user's complete marker (marker data + report data)
  static async updateUserMarker(
    markerId: string,
    updateData: MarkerUpdateData
  ): Promise<Marker> {
    const response = await api.put<ApiResponse<Marker>>(
      `/markers/${markerId}/edit`,
      updateData
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to update marker');
    }

    return response.data.data;
  }

  // Update a user's report only
  static async updateUserReport(
    markerId: string, 
    reportId: string, 
    updateData: ReportUpdateData
  ): Promise<Marker> {
    const response = await api.put<ApiResponse<Marker>>(
      `/markers/${markerId}/reports/${reportId}/update`,
      updateData
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to update report');
    }

    return response.data.data;
  }

  // Delete a user's report
  static async deleteUserReport(markerId: string, reportId: string): Promise<Marker> {
    const response = await api.delete<ApiResponse<Marker>>(
      `/markers/${markerId}/reports/${reportId}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to delete report');
    }

    return response.data.data;
  }

  // Add support to a report (existing functionality)
  static async addSupportToReport(markerId: string, reportId: string): Promise<Marker> {
    const response = await api.post<ApiResponse<Marker>>(
      `/markers/${markerId}/reports/${reportId}/support`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to add support');
    }

    return response.data.data;
  }
}

export default ReportService;